import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { timeSlotsAPI } from '../utils/api';
import ConfirmModal from '../components/ConfirmModal';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function TimeSlotManagement() {
  const [activeTab, setActiveTab] = useState('slots'); // 'slots', 'templates', 'exceptions'
  const [slots, setSlots] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Bulk create state
  const [showBulkCreate, setShowBulkCreate] = useState(false);
  const [bulkData, setBulkData] = useState({
    start_date: '',
    end_date: '',
    time_slots: [
      { start_time: '09:00', end_time: '10:00' },
      { start_time: '10:00', end_time: '11:00' },
      { start_time: '11:00', end_time: '12:00' },
      { start_time: '13:00', end_time: '14:00' },
      { start_time: '14:00', end_time: '15:00' },
      { start_time: '15:00', end_time: '16:00' },
      { start_time: '16:00', end_time: '17:00' },
    ]
  });

  // Template form state
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    day_of_week: '',
    start_time: '09:00',
    end_time: '17:00',
    slot_duration: 60,
    break_start: '12:00',
    break_end: '13:00'
  });

  // Exception form state
  const [showExceptionForm, setShowExceptionForm] = useState(false);
  const [exceptionForm, setExceptionForm] = useState({
    date: '',
    end_date: '', // For date range closures
    is_closed: true,
    reason: ''
  });

  // Generate from templates form
  const [showGenerate, setShowGenerate] = useState(false);
  const [generateData, setGenerateData] = useState({
    start_date: '',
    end_date: '',
    template_ids: [] // Selected template IDs
  });

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    loading: false
  });

  useEffect(() => {
    fetchSlots();
    fetchTemplates();
    fetchExceptions();
  }, [selectedDate]);

  const fetchSlots = async () => {
    setLoading(true);
    try {
      const response = await timeSlotsAPI.getAll(selectedDate);
      setSlots(response.data);
    } catch (error) {
      console.error('Error fetching slots:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await timeSlotsAPI.getTemplates();
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchExceptions = async () => {
    try {
      const response = await timeSlotsAPI.getExceptions();
      setExceptions(response.data);
    } catch (error) {
      console.error('Error fetching exceptions:', error);
    }
  };

  const handleBulkCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await timeSlotsAPI.createBulk(bulkData);
      toast.success('Time slots created successfully!');
      setShowBulkCreate(false);
      fetchSlots();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create time slots');
    } finally {
      setLoading(false);
    }
  };

  const toggleSlotAvailability = async (slotId, currentAvailability) => {
    try {
      await timeSlotsAPI.update(slotId, { available: !currentAvailability });
      fetchSlots();
      toast.success(currentAvailability ? 'Slot blocked' : 'Slot unblocked');
    } catch (error) {
      toast.error('Failed to update slot');
    }
  };

  const deleteSlot = (slotId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Time Slot',
      message: 'Are you sure you want to delete this time slot?',
      loading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, loading: true }));
        try {
          await timeSlotsAPI.delete(slotId);
          toast.success('Time slot deleted');
          setConfirmModal(prev => ({ ...prev, isOpen: false, loading: false }));
          fetchSlots();
        } catch (error) {
          toast.error('Failed to delete slot');
          setConfirmModal(prev => ({ ...prev, loading: false }));
        }
      }
    });
  };

  // Template handlers
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...templateForm,
        day_of_week: templateForm.day_of_week === '' ? null : parseInt(templateForm.day_of_week)
      };
      await timeSlotsAPI.createTemplate(data);
      toast.success('Template created successfully!');
      setShowTemplateForm(false);
      setTemplateForm({
        name: '',
        day_of_week: '',
        start_time: '09:00',
        end_time: '17:00',
        slot_duration: 60,
        break_start: '12:00',
        break_end: '13:00'
      });
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create template');
    }
  };

  const deleteTemplate = (templateId, templateName) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Template',
      message: `Are you sure you want to delete the template "${templateName}"?`,
      loading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, loading: true }));
        try {
          await timeSlotsAPI.deleteTemplate(templateId);
          toast.success('Template deleted');
          setConfirmModal(prev => ({ ...prev, isOpen: false, loading: false }));
          fetchTemplates();
        } catch (error) {
          toast.error('Failed to delete template');
          setConfirmModal(prev => ({ ...prev, loading: false }));
        }
      }
    });
  };

  const toggleTemplateActive = async (template) => {
    try {
      await timeSlotsAPI.updateTemplate(template.id, { ...template, active: !template.active });
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  // Exception handlers - supports date range
  const handleCreateException = async (e) => {
    e.preventDefault();
    try {
      // If end_date is set, create exceptions for each day in range
      if (exceptionForm.end_date && exceptionForm.end_date !== exceptionForm.date) {
        const startDate = new Date(exceptionForm.date);
        const endDate = new Date(exceptionForm.end_date);
        let currentDate = new Date(startDate);
        let created = 0;

        while (currentDate <= endDate) {
          const dateStr = currentDate.toISOString().split('T')[0];
          try {
            await timeSlotsAPI.createException({
              date: dateStr,
              is_closed: true,
              reason: exceptionForm.reason
            });
            created++;
          } catch (err) {
            // Skip if already exists
            console.log(`Skipped ${dateStr}: ${err.response?.data?.error || 'already exists'}`);
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }

        toast.success(`Created ${created} closure${created !== 1 ? 's' : ''} successfully!`);
      } else {
        // Single date
        await timeSlotsAPI.createException({
          date: exceptionForm.date,
          is_closed: true,
          reason: exceptionForm.reason
        });
        toast.success('Closure added successfully!');
      }

      setShowExceptionForm(false);
      setExceptionForm({ date: '', end_date: '', is_closed: true, reason: '' });
      fetchExceptions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create exception');
    }
  };

  const deleteException = (exceptionId, exceptionDate) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Closure',
      message: `Are you sure you want to remove the closure for ${exceptionDate}?`,
      loading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, loading: true }));
        try {
          await timeSlotsAPI.deleteException(exceptionId);
          toast.success('Closure removed');
          setConfirmModal(prev => ({ ...prev, isOpen: false, loading: false }));
          fetchExceptions();
        } catch (error) {
          toast.error('Failed to delete exception');
          setConfirmModal(prev => ({ ...prev, loading: false }));
        }
      }
    });
  };

  // Generate from templates
  const handleGenerateFromTemplates = async (e) => {
    e.preventDefault();
    if (generateData.template_ids.length === 0) {
      toast.error('Please select at least one template');
      return;
    }
    setLoading(true);
    try {
      const response = await timeSlotsAPI.generateFromTemplates(generateData);
      toast.success(response.data.message);
      setShowGenerate(false);
      setGenerateData({ start_date: '', end_date: '', template_ids: [] });
      fetchSlots();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to generate slots');
    } finally {
      setLoading(false);
    }
  };

  // Toggle template selection for generation
  const toggleTemplateSelection = (templateId) => {
    setGenerateData(prev => {
      const ids = prev.template_ids;
      if (ids.includes(templateId)) {
        return { ...prev, template_ids: ids.filter(id => id !== templateId) };
      } else {
        return { ...prev, template_ids: [...ids, templateId] };
      }
    });
  };

  // Select/deselect all templates
  const selectAllTemplates = () => {
    const activeTemplateIds = templates.filter(t => t.active).map(t => t.id);
    if (generateData.template_ids.length === activeTemplateIds.length) {
      setGenerateData(prev => ({ ...prev, template_ids: [] }));
    } else {
      setGenerateData(prev => ({ ...prev, template_ids: activeTemplateIds }));
    }
  };

  const closeConfirmModal = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
  };

  return (
    <div className="container">
      <h1 style={{ marginBottom: '2rem' }}>Time Slot Management</h1>

      {/* Tab Navigation - responsive wrap for mobile */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <button
          className={`btn ${activeTab === 'slots' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('slots')}
          style={{ flex: '1 1 auto', minWidth: '120px' }}
        >
          Time Slots
        </button>
        <button
          className={`btn ${activeTab === 'templates' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('templates')}
          style={{ flex: '1 1 auto', minWidth: '120px' }}
        >
          Templates
        </button>
        <button
          className={`btn ${activeTab === 'exceptions' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('exceptions')}
          style={{ flex: '1 1 auto', minWidth: '120px' }}
        >
          Closures
        </button>
      </div>

      {/* ==================== SLOTS TAB ==================== */}
      {activeTab === 'slots' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <h2 style={{ margin: 0 }}>Manage Availability</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowBulkCreate(!showBulkCreate)}
                style={{ whiteSpace: 'nowrap' }}
              >
                {showBulkCreate ? 'Cancel' : 'Bulk Create'}
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setShowGenerate(!showGenerate)}
                style={{ whiteSpace: 'nowrap' }}
              >
                {showGenerate ? 'Cancel' : 'Generate'}
              </button>
            </div>
          </div>

          {/* Generate from Templates Form */}
          {showGenerate && (
            <form onSubmit={handleGenerateFromTemplates} style={{ marginBottom: '2rem', padding: '1rem', background: '#e8f5e9', borderRadius: '4px', border: '1px solid #c8e6c9' }}>
              <h3 style={{ marginBottom: '1rem' }}>Generate Slots from Templates</h3>

              {/* Template Selection */}
              {templates.filter(t => t.active).length > 0 ? (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <label style={{ fontWeight: 600, color: '#2e7d32' }}>Select Templates to Use:</label>
                    <button
                      type="button"
                      onClick={selectAllTemplates}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#2e7d32',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        textDecoration: 'underline'
                      }}
                    >
                      {generateData.template_ids.length === templates.filter(t => t.active).length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.75rem', background: 'white', borderRadius: '4px', border: '1px solid #c8e6c9' }}>
                    {templates.filter(t => t.active).map(template => (
                      <label
                        key={template.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.5rem 0.75rem',
                          background: generateData.template_ids.includes(template.id) ? '#e8f5e9' : 'transparent',
                          border: '1px solid',
                          borderColor: generateData.template_ids.includes(template.id) ? '#81c784' : '#ddd',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={generateData.template_ids.includes(template.id)}
                          onChange={() => toggleTemplateSelection(template.id)}
                          style={{ width: '18px', height: '18px' }}
                        />
                        <div style={{ flex: 1 }}>
                          <strong style={{ color: '#333' }}>{template.day_of_week !== null ? DAY_NAMES[template.day_of_week] : 'All Days'}</strong>
                          <span style={{ color: '#666', marginLeft: '0.5rem' }}>
                            ({template.name})
                          </span>
                        </div>
                        <span style={{ color: '#666', fontSize: '0.875rem' }}>
                          {template.start_time} - {template.end_time}
                        </span>
                      </label>
                    ))}
                  </div>
                  {generateData.template_ids.length > 0 && (
                    <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#2e7d32' }}>
                      {generateData.template_ids.length} template{generateData.template_ids.length !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
              ) : (
                <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
                  <p style={{ color: '#856404', margin: 0 }}>
                    No active templates. Go to the "Recurring Templates" tab to create templates first.
                  </p>
                </div>
              )}

              <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
                <strong>How it works:</strong> Select a date range (e.g., "next month"). Slots will be created for each day that matches the selected template's day of week. For example, if you select a "Monday" template and a date range of Feb 1-28, slots will be created for every Monday in February.
              </p>
              <div className="grid grid-2">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={generateData.start_date}
                    onChange={(e) => setGenerateData({ ...generateData, start_date: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={generateData.end_date}
                    onChange={(e) => setGenerateData({ ...generateData, end_date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-success" disabled={loading || generateData.template_ids.length === 0}>
                {loading ? 'Generating...' : `Generate Slots (${generateData.template_ids.length} template${generateData.template_ids.length !== 1 ? 's' : ''})`}
              </button>
            </form>
          )}

          {/* Manual Bulk Create Form */}
          {showBulkCreate && (
            <form onSubmit={handleBulkCreate} style={{ marginBottom: '2rem', padding: '1rem', background: '#f8f9fa', borderRadius: '4px' }}>
              <h3 style={{ marginBottom: '1rem' }}>Manual Bulk Create</h3>
              <div className="grid grid-2">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={bulkData.start_date}
                    onChange={(e) => setBulkData({ ...bulkData, start_date: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={bulkData.end_date}
                    onChange={(e) => setBulkData({ ...bulkData, end_date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Time Slots (9 AM to 5 PM, excluding lunch)</label>
                <div style={{ fontSize: '0.875rem', color: '#666' }}>
                  {bulkData.time_slots.map((slot, i) => (
                    <span key={i}>{slot.start_time}-{slot.end_time}{i < bulkData.time_slots.length - 1 ? ', ' : ''}</span>
                  ))}
                </div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Creating...' : 'Create Slots'}
              </button>
            </form>
          )}

          {/* Date selector and slots table */}
          <div className="form-group">
            <label>Select Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="loading">Loading slots...</div>
          ) : slots.length === 0 ? (
            <div className="alert alert-info">
              No time slots for this date. Generate slots from templates or create manually.
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Start Time</th>
                    <th>End Time</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {slots.map(slot => (
                    <tr key={slot.id}>
                      <td>{slot.start_time}</td>
                      <td>{slot.end_time}</td>
                      <td>
                        <span className={slot.available ? 'badge badge-success' : 'badge badge-danger'}>
                          {slot.available ? 'Available' : 'Unavailable'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            className={`btn ${slot.available ? 'btn-secondary' : 'btn-success'}`}
                            style={{ fontSize: '0.875rem', padding: '0.5rem' }}
                            onClick={() => toggleSlotAvailability(slot.id, slot.available)}
                          >
                            {slot.available ? 'Block' : 'Unblock'}
                          </button>
                          <button
                            className="btn btn-danger"
                            style={{ fontSize: '0.875rem', padding: '0.5rem' }}
                            onClick={() => deleteSlot(slot.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ==================== TEMPLATES TAB ==================== */}
      {activeTab === 'templates' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 style={{ margin: 0 }}>Recurring Availability Templates</h2>
            <button
              className="btn btn-primary"
              onClick={() => setShowTemplateForm(!showTemplateForm)}
            >
              {showTemplateForm ? 'Cancel' : 'Add Template'}
            </button>
          </div>

          <p style={{ color: '#666', marginBottom: '1.5rem' }}>
            Templates define your regular working hours. Use "Generate from Templates" in the Time Slots tab to create slots automatically.
          </p>

          {/* Template Form */}
          {showTemplateForm && (
            <form onSubmit={handleCreateTemplate} style={{ marginBottom: '2rem', padding: '1rem', background: '#f8f9fa', borderRadius: '4px' }}>
              <h3 style={{ marginBottom: '1rem' }}>Create New Template</h3>
              <div className="form-group">
                <label>Template Name</label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g., Weekday Schedule"
                  required
                />
              </div>
              <div className="form-group">
                <label>Day of Week</label>
                <select
                  value={templateForm.day_of_week}
                  onChange={(e) => setTemplateForm({ ...templateForm, day_of_week: e.target.value })}
                >
                  <option value="">All Days</option>
                  {DAY_NAMES.map((day, index) => (
                    <option key={index} value={index}>{day}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label>Start Time</label>
                  <input
                    type="time"
                    value={templateForm.start_time}
                    onChange={(e) => setTemplateForm({ ...templateForm, start_time: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Time</label>
                  <input
                    type="time"
                    value={templateForm.end_time}
                    onChange={(e) => setTemplateForm({ ...templateForm, end_time: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Slot Duration (minutes)</label>
                <select
                  value={templateForm.slot_duration}
                  onChange={(e) => setTemplateForm({ ...templateForm, slot_duration: parseInt(e.target.value) })}
                >
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>60 minutes (1 hour)</option>
                  <option value={90}>90 minutes</option>
                  <option value={120}>120 minutes (2 hours)</option>
                </select>
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label>Break Start (optional)</label>
                  <input
                    type="time"
                    value={templateForm.break_start}
                    onChange={(e) => setTemplateForm({ ...templateForm, break_start: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Break End (optional)</label>
                  <input
                    type="time"
                    value={templateForm.break_end}
                    onChange={(e) => setTemplateForm({ ...templateForm, break_end: e.target.value })}
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-primary">Create Template</button>
            </form>
          )}

          {/* Templates List */}
          {templates.length === 0 ? (
            <div className="alert alert-info">
              No templates created yet. Click "Add Template" to create your first recurring schedule.
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Day</th>
                    <th>Hours</th>
                    <th>Slot</th>
                    <th>Break</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map(template => (
                    <tr key={template.id}>
                      <td><strong>{template.name}</strong></td>
                      <td>{template.day_of_week !== null ? DAY_NAMES[template.day_of_week] : 'All Days'}</td>
                      <td>{template.start_time} - {template.end_time}</td>
                      <td>{template.slot_duration} min</td>
                      <td>{template.break_start ? `${template.break_start}-${template.break_end}` : 'None'}</td>
                      <td>
                        <span className={template.active ? 'badge badge-success' : 'badge badge-danger'}>
                          {template.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            className={`btn ${template.active ? 'btn-secondary' : 'btn-success'}`}
                            style={{ fontSize: '0.875rem', padding: '0.5rem' }}
                            onClick={() => toggleTemplateActive(template)}
                          >
                            {template.active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="btn btn-danger"
                            style={{ fontSize: '0.875rem', padding: '0.5rem' }}
                            onClick={() => deleteTemplate(template.id, template.name)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ==================== EXCEPTIONS TAB ==================== */}
      {activeTab === 'exceptions' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 style={{ margin: 0 }}>Holidays & Closures</h2>
            <button
              className="btn btn-primary"
              onClick={() => setShowExceptionForm(!showExceptionForm)}
            >
              {showExceptionForm ? 'Cancel' : 'Add Closure'}
            </button>
          </div>

          <p style={{ color: '#666', marginBottom: '1.5rem' }}>
            Mark specific dates as closed. These dates will be skipped when generating slots from templates.
          </p>

          {/* Exception Form */}
          {showExceptionForm && (
            <form onSubmit={handleCreateException} style={{ marginBottom: '2rem', padding: '1rem', background: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
              <h3 style={{ marginBottom: '1rem' }}>Add Holiday or Closure</h3>
              <p style={{ color: '#856404', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Add a single date or a date range for longer closures (e.g., annual leave).
              </p>
              <div className="grid grid-2">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={exceptionForm.date}
                    onChange={(e) => setExceptionForm({ ...exceptionForm, date: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Date (optional)</label>
                  <input
                    type="date"
                    value={exceptionForm.end_date}
                    onChange={(e) => setExceptionForm({ ...exceptionForm, end_date: e.target.value })}
                    min={exceptionForm.date}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Reason (optional)</label>
                <input
                  type="text"
                  value={exceptionForm.reason}
                  onChange={(e) => setExceptionForm({ ...exceptionForm, reason: e.target.value })}
                  placeholder="e.g., Christmas Day, Annual Leave, Family Holiday"
                />
              </div>
              {exceptionForm.date && exceptionForm.end_date && exceptionForm.end_date !== exceptionForm.date && (
                <div style={{ marginBottom: '1rem', padding: '0.5rem 0.75rem', background: '#fff8e1', borderRadius: '4px', border: '1px solid #ffe082' }}>
                  <span style={{ fontSize: '0.875rem', color: '#7c6600' }}>
                    This will create closures for {Math.ceil((new Date(exceptionForm.end_date) - new Date(exceptionForm.date)) / (1000 * 60 * 60 * 24)) + 1} days
                  </span>
                </div>
              )}
              <button type="submit" className="btn btn-warning">
                {exceptionForm.end_date && exceptionForm.end_date !== exceptionForm.date
                  ? 'Add Date Range Closure'
                  : 'Add Single Day Closure'}
              </button>
            </form>
          )}

          {/* Exceptions List */}
          {exceptions.length === 0 ? (
            <div className="alert alert-info">
              No holidays or closures marked. Click "Add Closure" to mark dates as closed.
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Day</th>
                    <th>Reason</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {exceptions.map(exception => (
                    <tr key={exception.id}>
                      <td>{exception.date}</td>
                      <td>{DAY_NAMES[new Date(exception.date).getDay()]}</td>
                      <td>{exception.reason || '-'}</td>
                      <td>
                        <button
                          className="btn btn-danger"
                          style={{ fontSize: '0.875rem', padding: '0.5rem' }}
                          onClick={() => deleteException(exception.id, exception.date)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Delete"
        confirmStyle="danger"
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirmModal}
        loading={confirmModal.loading}
      />
    </div>
  );
}

export default TimeSlotManagement;
